/**
 * Print layout, pure: the paper a page is laid out on in screen pixels, and where its blocks
 * break onto the next sheet. The editor measures its blocks as they are drawn (without the
 * gaps it put between sheets), asks {@link paginate} where each sheet ends, and draws a gap
 * there: the rest of the sheet, its bottom margin with the page number, the desk between
 * sheets and the next one's top margin.
 */
import type { PageLayout } from "@protocol/PageLayout";
import type { PaperSize } from "@protocol/PaperSize";
import { MAX_MARGIN_MM, MEASURE_SLACK_PX, MIN_CONTENT_PX, NORMAL_MARGINS_MM, PAPER, PX_PER_MM, RISE_TRIES_PER_BLOCK } from "./constants";

/** Paper sizes for people. */
export const PAPER_LABELS: Record<PaperSize, string> = { a4: "A4 (21 × 29.7 cm)", a5: "A5 (14.8 × 21 cm)", letter: "Letter (8.5 × 11 in)", legal: "Legal (8.5 × 14 in)" };

/** A sheet in screen pixels. */
export interface Sheet {
  /** Its width. */
  width: number;
  /** Its height. */
  height: number;
  /** Its margins: top, right, bottom, left. */
  margins: [number, number, number, number];
  /** The height text may take on it. */
  content: number;
}

/** The sheet `layout` asks for. */
export function sheetOf(layout: PageLayout): Sheet {
  const [w, h] = PAPER[layout.size] ?? PAPER.a4;
  const [width, height] = (layout.orientation === "landscape" ? [h, w] : [w, h]).map((mm) => Math.round(mm * PX_PER_MM)) as [number, number];
  const margins = (layout.margins ?? NORMAL_MARGINS_MM).map((mm) => Math.round(Math.max(0, Math.min(MAX_MARGIN_MM, mm)) * PX_PER_MM)) as [number, number, number, number];
  return { width, height, margins, content: Math.max(MIN_CONTENT_PX, height - margins[0] - margins[2]) };
}

/** One block as drawn: where it starts (without the gaps), how much room it takes before the next, and whether it starts a sheet. */
export interface Measured {
  /** Its top, from the first block's, gaps left out. */
  top: number;
  /** The room it takes: to the next block's top (the last: its height). */
  extent: number;
  /** A page break: it starts a new sheet. */
  breakBefore: boolean;
  /**
   * How far below its top what it draws goes, when that is past its room: a picture beside the
   * words (square) or in front of or behind them, which the blocks after it do not make room for.
   */
  reach?: number;
  /** How far above its top what it draws starts: a picture placed above its own block. */
  rise?: number;
}

/** Where the sheets break. */
export interface Pagination {
  /** Before which block (by index) a sheet ends, and the room left blank on it. */
  breaks: { before: number; filler: number }[];
  /** How many sheets. */
  sheets: number;
  /** The room left blank at the end of the last sheet (at least down to the end of a picture reaching past its text). */
  tail: number;
  /**
   * Pictures drawn lower than placed, by block index, so they start on their sheet (in its top
   * margin at most): those rising above it beside a block no break can go before (Word keeps
   * a picture on its page). None when every picture fits.
   */
  lifts?: Record<number, number>;
}

/**
 * Where `blocks` (in order) break onto sheets with `content` pixels of room each: a block that
 * does not fit what is left goes to the next sheet, unless it is the first on its sheet (a
 * block taller than a sheet makes its sheet longer); a page break always starts one. A sheet
 * never ends across a picture: when the block that does not fit sits beside a picture reaching
 * below its top (the words wrapping around a square picture, or one in front or behind), the
 * picture's block goes to the next sheet with it, as Word moves a picture with its paragraph.
 * Nor does a picture start above the sheet its block is on (it may go into the top margin, `above`
 * pixels): the sheet then starts earlier, with the block the picture's top is beside, when all
 * from there to the picture's block fits on one sheet; else the picture is drawn lower
 * (`lifts`), starting at the sheet's top margin.
 */
export function paginate(blocks: readonly Measured[], content: number, above = 0): Pagination {
  const breaks: { before: number; filler: number }[] = [];
  if (!blocks.length) return { breaks, sheets: 1, tail: content };
  /** Where a block's drawing ends. */
  const bottom = (b: Measured) => b.top + Math.max(b.extent, b.reach ?? 0);
  /** The first block of the sheet being filled, and where that sheet starts. */
  let first = 0;
  let start = blocks[0]!.top;
  /** The first block of each sheet before, so a sheet can start earlier. */
  const firsts: number[] = [];
  /** Ends the sheet before block `at`. */
  const breakBefore = (at: number) => {
    breaks.push({ before: at, filler: Math.max(0, content - (blocks[at]!.top - start)) });
    firsts.push(first);
    first = at;
    start = blocks[at]!.top;
  };
  /** Takes back the last break: the sheet before goes on. */
  const unbreak = () => {
    breaks.pop();
    first = firsts.pop()!;
    start = blocks[first]!.top;
  };
  // Each sheet may start earlier only so often: never round and round.
  let budget = blocks.length * RISE_TRIES_PER_BLOCK;
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]!;
    if (i > first && block.breakBefore) breakBefore(i);
    const rises = block.top - (block.rise ?? 0);
    if (firsts.length && rises < start - above - MEASURE_SLACK_PX && !block.breakBefore && !blocks[first]!.breakBefore && budget-- > 0) {
      // Its picture starts on the sheet before: this sheet starts with the block beside the picture's top.
      const before = firsts[firsts.length - 1]!;
      let at = -1;
      for (let k = first - 1; k > before; k--) {
        if (blocks[k]!.top <= rises + MEASURE_SLACK_PX) {
          at = k;
          break;
        }
      }
      // Only when it helps: what goes on the sheet from there must fit, or the break comes back (and leaves a blank sheet).
      if (at > before && bottom(block) - blocks[at]!.top <= content + MEASURE_SLACK_PX) {
        unbreak();
        breakBefore(at);
        i = at - 1;
        continue;
      }
    }
    if (i === first || bottom(block) - start <= content + MEASURE_SLACK_PX) continue;
    // Not on this sheet: it starts the next, with any picture beside it.
    let at = i;
    for (let k = first + 1; k < i; k++) {
      if (bottom(blocks[k]!) > block.top + MEASURE_SLACK_PX && !blocks.slice(k + 1, i + 1).some((b) => b.breakBefore)) {
        at = k;
        break;
      }
    }
    breakBefore(at);
    // Looked at again as the first of its sheet (a picture above it may move the break up).
    i = at - 1;
  }
  // Pictures still rising above their sheet's top margin: drawn lower, from it.
  const lifts: Record<number, number> = {};
  let sheet = 0;
  let sheetStart = blocks[0]!.top;
  blocks.forEach((b, i) => {
    if (sheet < breaks.length && breaks[sheet]!.before === i) {
      sheetStart = b.top;
      sheet++;
    }
    const by = sheetStart - above - (b.top - (b.rise ?? 0));
    if ((b.rise ?? 0) > 0 && by > MEASURE_SLACK_PX) lifts[i] = Math.round(by);
  });
  // The last sheet ends after its room, or lower when a picture reaches further.
  const flow = Math.max(...blocks.slice(first).map((b) => b.top + b.extent));
  const drawn = Math.max(...blocks.slice(first).map(bottom));
  const laid: Pagination = { breaks, sheets: 1 + breaks.length, tail: Math.max(0, content - (flow - start), drawn - flow) };
  return Object.keys(lifts).length ? { ...laid, lifts } : laid;
}

/** Whether two paginations put the breaks in the same places (and draw the same pictures lower). */
export function samePagination(a: Pagination, b: Pagination): boolean {
  return JSON.stringify(a.lifts ?? {}) === JSON.stringify(b.lifts ?? {}) && a.sheets === b.sheets && Math.abs(a.tail - b.tail) < 1 && a.breaks.length === b.breaks.length && a.breaks.every((x, i) => x.before === b.breaks[i]!.before && Math.abs(x.filler - b.breaks[i]!.filler) < 1);
}
